export interface Clinic {
    id: number;
    name: string;
    description: string;
    organization: string;
    created_at: string;
    updated_at: string;
    cohorts: Cohort[];
}

export interface Cohort {
    id: number;
    name: string;
    description: string;
    clinic: number;
    organization: string;
    created_at: string;
    updated_at: string;
    patient_count: number;
}

// Extended interfaces for access control that include user details
export interface ClinicAccessWithUserDetails {
    id: number;
    user_id: number;
    user: {
        id: number;
        username: string;
        first_name: string;
        last_name: string;
        email: string;
        role?: {
            roletype: string;
            name: string;
        };
    };
    clinic: number;
    clinic_name: string;
    access_level: AccessLevel;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface CohortAccessWithUserDetails {
    id: number;
    user_id: number;
    user: {
        id: number;
        username: string;
        first_name: string;
        last_name: string;
        email: string;
        role?: {
            roletype: string;
            name: string;
        };
    };
    cohort: number;
    cohort_name: string;
    clinic_name: string;
    access_level: AccessLevel;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface CohortPatientAssociation {
    id: number;
    cohort: number;
    patient_id: string;
    assigned_by: number;
    notes: string;
    created_at: string;
    updated_at: string;
}

export type AccessLevel = 'read' | 'write' | 'admin';

export interface GrantAccessRequest {
    user_id: number;
    access_level: AccessLevel;
}

export interface GrantCohortAccessRequest {
    user_id: number;
    cohort: number;
    access_level: AccessLevel;
}

export interface BulkPatientAssignmentRequest {
    cohort: number;
    patient_ids: string[];
    notes: string;
}

export interface PatientAssignmentRequest {
    cohort: number;
    patient_id: string;
    notes: string;
}

// For user selection in dialogs
export interface UserOption {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    role?: {
        roletype: string;
        name: string;
    };
}

// For access level selection
export interface AccessLevelOption {
    value: AccessLevel;
    label: string;
    description: string;
}

export const ACCESS_LEVEL_OPTIONS: AccessLevelOption[] = [
    {
        value: 'read',
        label: 'Read Only',
        description: 'Can view patients and data but cannot modify'
    },
    {
        value: 'write',
        label: 'Read & Write',
        description: 'Can view and modify patient data and assignments'
    },
    {
        value: 'admin',
        label: 'Administrator',
        description: 'Full access including managing other users\' access'
    }
];

// Helper function to get display name for user
export const getUserDisplayName = (user: UserOption): string => {
    return `${user.first_name} ${user.last_name} (${user.username})`;
};

// Helper function to get access level display
export const getAccessLevelDisplay = (level: AccessLevel): string => {
    const option = ACCESS_LEVEL_OPTIONS.find(opt => opt.value === level);
    return option ? option.label : level;
};